/**
 * useEmailFolderAPI Hook
 *
 * Provides API methods for email folder CRUD operations.
 */

import { useState, useCallback } from 'react'
import type { EmailFolder, EmailFolderCreate, EmailFolderPatch } from '@/lib/email-templates/folder-types'

const getBackendUrl = (): string => {
  if (import.meta.env.VITE_API_URL) {
    return import.meta.env.VITE_API_URL
  }
  return ''
}

interface UseEmailFolderAPIResult {
  isLoading: boolean
  error: string | null
  createFolder: (data: EmailFolderCreate) => Promise<EmailFolder | null>
  patchFolder: (folderId: number, data: EmailFolderPatch) => Promise<EmailFolder | null>
  deleteFolder: (folderId: number) => Promise<boolean>
  clearError: () => void
}

export function useEmailFolderAPI(): UseEmailFolderAPIResult {
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const clearError = useCallback(() => setError(null), [])

  const createFolder = useCallback(async (data: EmailFolderCreate): Promise<EmailFolder | null> => {
    setIsLoading(true)
    setError(null)

    try {
      const backendUrl = getBackendUrl()
      const response = await fetch(`${backendUrl}/api/equip/email-folders`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data),
      })

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}))
        throw new Error(errorData.detail || `Failed to create folder: ${response.status}`)
      }

      return await response.json()
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to create folder'
      setError(message)
      return null
    } finally {
      setIsLoading(false)
    }
  }, [])

  const patchFolder = useCallback(async (folderId: number, data: EmailFolderPatch): Promise<EmailFolder | null> => {
    setIsLoading(true)
    setError(null)

    try {
      const backendUrl = getBackendUrl()
      const response = await fetch(`${backendUrl}/api/equip/email-folders/${folderId}`, {
        method: 'PATCH',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data),
      })

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}))
        if (response.status === 404) throw new Error('Folder not found')
        throw new Error(errorData.detail || `Failed to update folder: ${response.status}`)
      }

      return await response.json()
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to update folder'
      setError(message)
      return null
    } finally {
      setIsLoading(false)
    }
  }, [])

  const deleteFolder = useCallback(async (folderId: number): Promise<boolean> => {
    setIsLoading(true)
    setError(null)

    try {
      const backendUrl = getBackendUrl()
      const response = await fetch(`${backendUrl}/api/equip/email-folders/${folderId}`, {
        method: 'DELETE',
        credentials: 'include',
      })

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}))
        if (response.status === 409) {
          throw new Error(errorData.detail || 'Folder is not empty. Move or delete its contents first.')
        }
        if (response.status === 404) throw new Error('Folder not found')
        throw new Error(errorData.detail || `Failed to delete folder: ${response.status}`)
      }

      return true
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to delete folder'
      setError(message)
      return false
    } finally {
      setIsLoading(false)
    }
  }, [])

  return { isLoading, error, createFolder, patchFolder, deleteFolder, clearError }
}
